import { useMemo } from 'react';
import { getNavIcon } from '@/lib/navigation-icons';
import { useNavigationApi, resolveNodeRoute, resolveIcon, type NavigationGroup } from './useNavigationApi';
import { getStoredBrand } from '@/lib/brandThemes';

const THOR_HIDDEN_MENUS = ['Parts', 'Service', 'Sales'];


/** Re-export NavigationGroup for consumers */
export type { NavigationGroup } from './useNavigationApi';

// ─── Mock data (matches the existing hardcoded navigation) ───

const unitsDropdownItems = [
  { name: "VIN/Unit Search", path: "/units", tag: "UnitSearch" },
  { name: "Inventory Transfers", path: "/inventory-transfers", tag: "InventoryTransfers" },
];

const salesDropdownItems = [
  { name: "Unit/Warranty Registration", path: "/warranty-registration", tag: "WarrantyRegistration" },
  { name: "My Claimed SPIFF's", path: "/my-spiffs", tag: "MyClaimedSpiffs" },
  { name: "Dealer Claimed SPIFF's", path: "/claimed-dealer-spiffs", tag: "DealerClaimedSpiffs" },
  { name: "Sales Person SPIFF's", path: "/salesperson-spiffs", tag: "SalesPersonSpiffs" },
  { name: "Dealer SPIFF's", path: "/dealer-spiffs", tag: "DealerSpiffs" },
  { name: "Dealer Invoices", path: "/dealer-invoices", tag: "DealerInvoices" },
];

const serviceDropdownItems = [
  { name: "Service Campaigns", path: "/service-campaigns", tag: "ServiceCampaigns" },
  { name: "RGA's", path: "/rga-list", tag: "Rgas" },
  { name: "Claims and Pre-Auths", path: "/dealer-claims", tag: "ClaimsAndPreAuths" },
  { name: "Your Tickets", path: "/your-tickets", tag: "YourTickets" },
  { name: "Labor Operations Library", path: "/labor-operations", tag: "LaborOperationsLibrary" },
];

const partsDropdownItems = [
  { name: "Quick Order", path: "/quick-order", tag: "QuickOrder" },
  { name: "Unknown Part Request", path: "/unknown-part-request", tag: "UnknownPartRequest" },
];

interface MockNavItem {
  name: string;
  type: 'dropdown' | 'link';
  path?: string;
  items?: Array<{ name: string; path: string }>;
  isPartsMenu?: boolean;
}

const baseMockNavigationItems: MockNavItem[] = [
  { name: "Parts", type: "dropdown", items: partsDropdownItems, isPartsMenu: true },
  { name: "Units", type: "dropdown", items: unitsDropdownItems as any },
  { name: "Service", type: "dropdown", items: serviceDropdownItems },
  { name: "Sales", type: "dropdown", items: salesDropdownItems },
  { name: "Message Board", path: "/message-board", type: "link" },
  { name: "Digital Assets", path: "/digital-assets", type: "link" },
];

export function useMainNavigation() {
  const { nodes, isLoading, useMock } = useNavigationApi({
    rootNavigationTag: 'MainMenu',
    stopLevel: 3,
  });

  const activeBrand = getStoredBrand();
  const isThor = activeBrand === 'Thor';

  // Build API-driven navigation groups from nodes
  const apiGroups = useMemo<NavigationGroup[] | null>(() => {
    if (!nodes) return null;
    return nodes
      .filter((node: any) => node.showInMenu !== false)
      .filter((node: any) => !isThor || !THOR_HIDDEN_MENUS.includes(node.name || ''))
      .map((node: any) => {
        const title = node.name || '';
        const children = (node.nodes || [])
          .filter((child: any) => child.showInMenu !== false)
          .map((child: any) => {
            const childTitle = child.name || '';
            return {
              title: childTitle,
              url: resolveNodeRoute(child),
              icon: resolveIcon(child.navigationTag),
            };
          });

        const hasChildren = children.length > 0;
        const resolvedUrl = resolveNodeRoute(node);

        return {
          title,
          type: hasChildren ? 'dropdown' : 'link',
          url: (node.isClickable === false && (hasChildren || title !== 'Message Board')) ? '#' : resolvedUrl,
          icon: resolveIcon(node.navigationTag),
          children,
        } as NavigationGroup;
      });
  }, [nodes, isThor]);

  const mockGroups = useMemo(() => {
    if (isThor) {
      return baseMockNavigationItems.filter(item => !THOR_HIDDEN_MENUS.includes(item.name));
    }
    return baseMockNavigationItems;
  }, [isThor]);

  return {
    /** API-driven groups (null if useMock or not yet loaded) */
    apiGroups,
    /** Mock navigation items */
    mockItems: mockGroups,
    isLoading,
    useMock,
  };
}
